import { User } from 'services/interface';
import { isInOrganization, isPaidUser } from 'services/accessControl';

export type RateLimitKind = 'hourly' | 'daily';
export type LimitAudience = 'unauthenticated' | 'community' | 'commercial';

type RateLimitError = {
    message?: string;
    messages?: string[];
    response?: {
        status?: number;
        headers?: Record<string, unknown>;
        data?: { error?: { message?: string; messages?: string[]; details?: { message?: string } } };
    };
};

/** Axios lowercases header keys, but a mocked or normalised error may not. */
const readHeader = (headers: Record<string, unknown> | undefined, name: string): unknown =>
    Object.entries(headers ?? {}).find(([key]) => key.toLowerCase() === name)?.[1];

/**
 * The sentence the backend wrote about this rejection ("Your request has been throttled, maximum
 * requests per hour: 12 for unregistered user"), which is what the prompt shows: it names the real
 * limit and the real account tier, which no static copy can. The precedence mirrors the toast path
 * in `src/services/api/FileUpload.ts`. Axios' own `error.message` ("Request failed with status code
 * 429") is deliberately left out — it is transport noise, not copy for a user.
 */
export const getRateLimitMessage = (error: unknown): string | null => {
    const { messages, response } = (error ?? {}) as RateLimitError;
    const { details, message, messages: errorMessages } = response?.data?.error ?? {};

    return details?.message || message || errorMessages?.[0] || messages?.[0] || null;
};

const DAILY_PATTERN = /daily|per day|24 ?hours|quota/;
// `rate limit` is deliberately absent: it is the opening of the gateway's *quota* sentence ("Rate
// limit exceeded, retry after the limit is reset. The limit will be reset in 1 day"), whose tail
// varies with the time left ("5 hours", "30 minutes"), so it must fall through to the daily default.
const HOURLY_PATTERN = /throttl|per (second|minute|hour)|too many requests/;

/**
 * The gateway (`mcl-metadefender-rest`) stamps every quota rejection, code 429000, with
 * `X-RateLimit-Interval`: the window length in seconds (86400 daily, 2592000 monthly), and lists it
 * in `CORS.EXPOSE_HEADERS`, so it is readable cross-origin. The hourly throttle, code 429001, sends
 * no `X-RateLimit-*` header at all, and `Retry-After` is never sent (and would not be exposed), so
 * the header is the one reliable signal; the wording is only a fallback for a stripped header.
 *
 * The daily patterns are checked first because `too many requests` is the generic 429 reason
 * phrase and would otherwise swallow a daily rejection.
 */
export const getRateLimitKind = (error: unknown): RateLimitKind | null => {
    const { message, response } = (error ?? {}) as RateLimitError;

    if (response?.status !== 429) {
        return null;
    }

    const intervalSeconds = Number(readHeader(response.headers, 'x-ratelimit-interval'));

    if (intervalSeconds > 0) {
        return intervalSeconds <= 3600 ? 'hourly' : 'daily';
    }

    const text = [getRateLimitMessage(error), message]
        .filter((part): part is string => Boolean(part))
        .join(' ')
        .toLowerCase();

    if (DAILY_PATTERN.test(text)) {
        return 'daily';
    }

    if (HOURLY_PATTERN.test(text)) {
        return 'hourly';
    }

    // With no header and no recognisable wording, the daily copy is the conservative choice: it
    // points at the limits/licensing page instead of promising a retry window the response never
    // confirmed.
    return 'daily';
};

/**
 * "Commercial" has three candidate signals in this codebase (`paid_user`, `licenseInfo.paidUser`,
 * org membership). Any paid user **or** any organization member counts as commercial here,
 * deliberately, so a paid org member on a non-`Commercial` license tier is never shown community
 * upgrade copy (MDCI-1021 Impact).
 */
export const getLimitAudience = (user: User | null | undefined): LimitAudience => {
    if (!user?.isLoggedIn) {
        return 'unauthenticated';
    }

    return isPaidUser(user) || isInOrganization(user) ? 'commercial' : 'community';
};
